import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * What a page looks like when there is nothing on it.
 *
 * Most screens in this application are empty most of the time, and that is the
 * system working: an empty ticket board at four in the afternoon means the
 * day's faults are dealt with. A plain white rectangle says the opposite — it
 * reads as broken, or as a page that failed to load.
 *
 * So three things, always:
 *
 *  1. **A patterned canvas** behind it — flat navy geometry at about 5%, from
 *     `.jua-canvas`. Enough that the page is obviously finished rather than
 *     unpainted, faint enough that anything placed on top stays fully readable.
 *  2. **A headline naming what belongs here**, not what is absent. "No open
 *     tickets", never "Nothing to show".
 *  3. **One line of body text and one action.** The body says when things will
 *     appear; the action is the single orange element on the screen.
 *
 * Written as an invitation rather than an apology. Nobody needs to be told the
 * software is sorry.
 */
export function EmptyState({
  icon: Icon,
  headline,
  body,
  action,
  secondary,
}: {
  /** A lucide icon. Navy, outlined, quiet — it is not the focus. */
  icon?: LucideIcon;
  /** Names what belongs here. Sentence case. */
  headline: string;
  /** One line. When things will appear, or what to do to make them. */
  body: string;
  /**
   * The primary action, and the only orange on the screen.
   *
   * Optional, because some empty states have no useful action — a completed
   * queue does not want a button — and a page whose only orange element is a
   * pointless one is worse than a page with none.
   */
  action?: { label: string; to?: string; onClick?: () => void };
  /** A quieter alternative. Navy outline, never orange. */
  secondary?: { label: string; to?: string; onClick?: () => void };
}) {
  return (
    <div
      className="jua-canvas flex min-h-80 flex-col items-center justify-center rounded-xl
        border border-border px-6 py-14 text-center"
    >
      {Icon && (
        <div
          className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl border
            border-navy-200 bg-card"
        >
          <Icon className="h-6 w-6 text-navy-500" aria-hidden={true} />
        </div>
      )}

      {/*
        A real heading element, not a styled div. Somebody navigating by
        headings should find out what this page is for without reading it.
      */}
      <h3 className="font-heading text-xl text-navy-700">{headline}</h3>

      {/* max-w keeps it near 60 characters, which is where a line stops being
          a paragraph you have to track back across. */}
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{body}</p>

      {(action || secondary) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {action &&
            (action.to ? (
              <Link to={action.to} className="btn-primary">
                {action.label}
              </Link>
            ) : (
              <button type="button" className="btn-primary" onClick={action.onClick}>
                {action.label}
              </button>
            ))}

          {secondary &&
            (secondary.to ? (
              <Link to={secondary.to} className="btn-secondary">
                {secondary.label}
              </Link>
            ) : (
              <button type="button" className="btn-secondary" onClick={secondary.onClick}>
                {secondary.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * The same thing, for a slot inside a card that has other content around it.
 *
 * No border and no minimum height, because it is already inside a bordered
 * surface and a box inside a box reads as a mistake. It keeps the pattern,
 * because that is what stops the gap looking like a rendering failure.
 */
export function EmptySlot({
  headline,
  body,
}: {
  headline: string;
  body?: string;
}) {
  return (
    <div className="jua-canvas rounded-xl px-5 py-10 text-center">
      <p className="font-heading text-base text-navy-700">{headline}</p>
      {body && <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>}
    </div>
  );
}
